import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Markdown } from "@/components/Markdown";
import { ShareButtons } from "@/components/ShareButtons";

const fmtDate = (d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/blogs/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="py-32 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-brand" /></div>;
  if (notFound || !post) return (
    <div className="py-32 text-center">
      <p className="text-brand-muted">This blog post could not be found.</p>
      <Link to="/blog" className="mt-4 inline-block text-brand-terracotta underline">Back to Blog</Link>
    </div>
  );

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    image: post.cover_image,
    datePublished: post.created_at,
    dateModified: post.updated_at,
    author: { "@type": "Organization", name: post.author },
  };

  return (
    <div data-testid="blog-post-page">
      <Seo title={post.title} path={`/blog/${post.slug}`} description={post.excerpt} image={post.cover_image} jsonLd={jsonLd} />
      <article className="mx-auto max-w-3xl px-6 py-14">
        <Link to="/blog" data-testid="back-to-blog" className="inline-flex items-center gap-1.5 text-sm text-brand-muted hover:text-brand mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to Blog
        </Link>
        <p className="text-xs text-brand-muted uppercase tracking-wider">{fmtDate(post.created_at)} · {post.author}</p>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl text-brand leading-[1.1]">{post.title}</h1>
        {post.tags?.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-2">
            {post.tags.map((t) => <span key={t} className="px-3 py-1 rounded-full bg-brand-surface border border-brand/10 text-xs text-brand font-medium">{t}</span>)}
          </div>
        )}
        <div className="mt-6"><ShareButtons title={post.title} /></div>
        {post.cover_image && (
          <img src={post.cover_image} alt={post.title} className="mt-8 rounded-sm w-full h-[420px] object-cover" />
        )}
        <div className="mt-10">
          <Markdown>{post.body}</Markdown>
        </div>

        {post.gallery?.length > 0 && (
          <div className="mt-10" data-testid="blog-gallery">
            <h2 className="font-display text-xl text-brand mb-4">Gallery</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {post.gallery.map((src, i) => (
                <a key={src} href={src} target="_blank" rel="noreferrer noopener" className="block rounded-sm overflow-hidden bg-brand-surface">
                  <img src={src} alt={`${post.title} photo ${i + 1}`} className="w-full h-56 object-cover hover:scale-105 transition-transform duration-500" />
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10 pt-8 border-t border-brand/10 flex items-center justify-between gap-4 flex-wrap">
          <ShareButtons title={post.title} />
          <Link to="/blog" className="text-sm text-brand-terracotta underline underline-offset-2">← All posts</Link>
        </div>
      </article>
    </div>
  );
}
